import { Button, Modal } from '@braskai/ui-kit';

import { alpha, css, styled } from '@mui/material';

import { touchHitArea } from '@/shared/lib/touchHitArea';

import type { PaywallCardLayout } from './PaywallCard';

type CardProps = {
  $layout: PaywallCardLayout;
};

type BadgeProps = {
  $tone: 'brand' | 'warning';
};

type StripProps = {
  $tallLine: boolean;
};

const CARD_PADDING: Record<PaywallCardLayout, string> = {
  dialog: '40px',
  inline: '28px 24px 24px',
  sheet: '32px 20px 24px',
};

const CARD_RADIUS: Record<PaywallCardLayout, string> = {
  dialog: '20px',
  inline: '12px',
  sheet: '20px 20px 0 0',
};

export const SDialog = styled(Modal)(
  ({ theme }) => css`
    .MuiBackdrop-root {
      background-color: ${alpha(theme.palette.grey[900], 0.45)};
      backdrop-filter: blur(3px);
    }

    // The card scrolls, not the paper: a transparent paper put its scrollbar outside the card's rounded corner.
    .MuiDialog-paper {
      overflow: hidden;
      border-radius: 20px;
      background-color: transparent;
      box-shadow:
        0 24px 64px -12px ${alpha(theme.palette.grey[900], 0.35)},
        0 0 0 1px ${alpha(theme.palette.grey[900], 0.04)};

      ${theme.breakpoints.up('tablet')} {
        max-width: 520px;
      }
    }
  `,
);

export const SCard = styled('div', {
  shouldForwardProp: (prop) => prop !== '$layout',
})<CardProps>(
  ({ $layout, theme }) => css`
    position: relative;
    width: 100%;
    max-width: ${$layout === 'sheet' ? '100%' : '520px'};
    padding: ${CARD_PADDING[$layout]};
    border: ${$layout === 'inline' ? `1px solid ${theme.palette.grey[200]}` : 'none'};
    border-radius: ${CARD_RADIUS[$layout]};
    background-color: ${theme.palette.common.white};
    color: ${theme.palette.grey[900]};
    box-sizing: border-box;

    // The gap between the card's blocks (description, preview, bullets, strip), tighter on a short screen.
    --paywall-gap: 20px;

    ${$layout !== 'inline' &&
    css`
      animation: paywall-card-fade-up 0.22s ease;
    `}

    ${$layout === 'dialog' &&
    css`
      max-height: calc(100vh - 64px);
      max-height: calc(100dvh - 64px);
      overflow-y: auto;
      overscroll-behavior: contain;
      scrollbar-width: thin;
      scrollbar-color: ${theme.palette.grey[300]} transparent;

      @media (max-height: 820px) {
        --paywall-gap: 14px;
        padding: 28px 40px;
      }
    `}

    @keyframes paywall-card-fade-up {
      from {
        transform: translateY(10px);
        opacity: 0;
      }
      to {
        transform: none;
        opacity: 1;
      }
    }
  `,
);

export const SCloseButton = styled('button')(
  ({ theme }) => css`
    position: absolute;
    top: 22px;
    right: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: ${theme.palette.grey[400]};
    background: transparent;
    font-family: ${theme.typography.fontFamily};
    cursor: pointer;

    &:hover {
      color: ${theme.palette.grey[700]};
      background-color: ${theme.palette.grey[100]};
    }

    ${touchHitArea()};
  `,
);

export const SBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone',
})<BadgeProps>(
  ({ $tone, theme }) => css`
    display: inline-flex;
    align-items: center;
    height: 26px;
    margin-bottom: 18px;
    padding: 0 12px;
    border-radius: 999px;
    color: ${$tone === 'warning' ? theme.palette.warning[600] : '#5a43d6'};
    background-color: #edeafb;
    font-weight: 600;
    font-size: 11px;
    line-height: 1;
    letter-spacing: 0.06em;
  `,
);

export const STitle = styled('h3', {
  shouldForwardProp: (prop) => prop !== '$layout',
})<CardProps>(
  ({ $layout, theme }) => css`
    margin: 0 0 ${$layout === 'inline' ? 10 : 12}px;
    color: ${theme.palette.grey[900]};
    font-weight: 700;
    font-size: ${$layout === 'inline' ? 26 : 30}px;
    line-height: ${$layout === 'inline' ? 32 : 36}px;
    letter-spacing: -0.02em;
  `,
);

export const SDescription = styled('p')(
  ({ theme }) => css`
    margin: 0 0 var(--paywall-gap, 20px);
    color: ${theme.palette.grey[500]};
    font-weight: 400;
    font-size: 15px;
    line-height: 22px;
    text-wrap: pretty;
  `,
);

export const SBullets = styled('div')(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: var(--paywall-gap, 20px);
  `,
);

export const SBullet = styled('div')(
  () => css`
    display: flex;
    align-items: flex-start;
    gap: 12px;
  `,
);

export const SBulletIcon = styled('svg')(
  ({ theme }) => css`
    flex-shrink: 0;
    margin-top: 1px;
    color: ${theme.palette.primary[600]};
  `,
);

export const SBulletText = styled('span')(({ theme }) => ({
  ...theme.typography['body-2'],
  color: theme.palette.grey[900],
  fontWeight: 400,
}));

export const SStrip = styled('div', {
  shouldForwardProp: (prop) => prop !== '$tallLine',
})<StripProps>(
  ({ $tallLine, theme }) => css`
    margin-bottom: var(--paywall-gap, 20px);
    padding: ${$tallLine ? '15px 16px 14px' : '15px 16px 13px'};
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 10px;
    background-color: ${theme.palette.grey[50]};
    color: ${theme.palette.grey[700]};
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
  `,
);

export const SCtaButton = styled(Button)(
  ({ theme }) => css`
    height: 52px;
    border-radius: 12px;
    box-shadow: none;
    font-weight: 600;
    font-size: 15px;
    line-height: 20px;

    &:hover {
      border-color: ${theme.palette.primary[700]};
      background-color: ${theme.palette.primary[700]};
    }
  `,
);
